import React, { useEffect, useState } from "react";
import { Badge, Button, Form, InputGroup, Spinner } from "react-bootstrap";
import axios from "axios";
import Cookies from "js-cookie";
import { Link } from "react-router-dom";
import { BsPlusLg } from "react-icons/bs";
import { baseURL } from "../../../helpers/baseURL";
import { EmbedFormBtn } from "./embedFormButtons";
import { normalizeTrustedDomainInput } from "./embedTrustedDomainUtils";
import { handleEmbedAuthRedirect } from "./embedAuthRedirect";

const authHeaders = () => ({
    Authorization: `Bearer ${Cookies.get("access_token")}`,
});

/**
 * Multi-select of workspace trusted domains for embed form editor.
 * Includes an inline "add domain" form so users don't have to leave the wizard.
 */
const EmbedTrustedDomainPicker = ({ value, onChange, onOptionsLoaded }) => {
    const selected = Array.isArray(value) ? value.map(Number) : [];
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newDomain, setNewDomain] = useState("");
    const [adding, setAdding] = useState(false);
    const [addError, setAddError] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await axios.get(
                    `${baseURL}ticket-embed-trusted-domains`,
                    { headers: authHeaders() }
                );
                if (!cancelled) {
                    const list = Array.isArray(data) ? data : [];
                    setOptions(list);
                    if (typeof onOptionsLoaded === "function") {
                        onOptionsLoaded(list);
                    }
                }
            } catch (e) {
                if (!cancelled && handleEmbedAuthRedirect(e)) {
                    return;
                }
                if (!cancelled) {
                    setOptions([]);
                    if (typeof onOptionsLoaded === "function") {
                        onOptionsLoaded([]);
                    }
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const toggle = (id) => {
        const n = Number(id);
        if (selected.includes(n)) {
            onChange(selected.filter((x) => x !== n));
        } else {
            onChange([...selected, n]);
        }
    };

    const openAddForm = () => {
        setAddError("");
        setNewDomain("");
        setShowAddForm(true);
    };

    const cancelAdd = () => {
        setShowAddForm(false);
        setNewDomain("");
        setAddError("");
    };

    const submitNewDomain = async (ev) => {
        if (ev) {
            ev.preventDefault();
            ev.stopPropagation();
        }
        const domain = normalizeTrustedDomainInput(newDomain);
        if (!domain) {
            setAddError("Enter a domain hostname.");
            return;
        }
        setAdding(true);
        setAddError("");
        try {
            const { data: created } = await axios.post(
                `${baseURL}ticket-embed-trusted-domain`,
                { domain },
                { headers: authHeaders() }
            );
            const { data: refreshed } = await axios.get(
                `${baseURL}ticket-embed-trusted-domains`,
                { headers: authHeaders() }
            );
            const list = Array.isArray(refreshed) ? refreshed : [];
            setOptions(list);
            if (typeof onOptionsLoaded === "function") {
                onOptionsLoaded(list);
            }
            if (created && created.id) {
                const newId = Number(created.id);
                if (!selected.includes(newId)) {
                    onChange([...selected, newId]);
                }
            }
            setShowAddForm(false);
            setNewDomain("");
        } catch (e) {
            if (handleEmbedAuthRedirect(e)) {
                return;
            }
            setAddError(
                e.response?.data?.message ||
                    "Could not save domain. It must be valid and reachable."
            );
        } finally {
            setAdding(false);
        }
    };

    return (
        <div className="embed-trusted-domain-picker">
            {loading ? (
                <div className="embed-trusted-domain-picker__loading">
                    <Spinner animation="border" size="sm" />
                </div>
            ) : options.length === 0 ? (
                <p className="text-muted small mb-2 embed-trusted-domain-picker__empty">
                    No trusted domains yet. Add one below to get started.
                </p>
            ) : (
                options.map((opt) => {
                    const id = Number(opt.id);
                    const checked = selected.includes(id);
                    const inactive = !opt.is_active;
                    return (
                        <Form.Check
                            key={id}
                            type="checkbox"
                            id={`embed-trusted-domain-${id}`}
                            className={
                                inactive
                                    ? "embed-trusted-domain-picker__item text-muted"
                                    : "embed-trusted-domain-picker__item"
                            }
                            checked={checked}
                            onChange={() => toggle(id)}
                            label={
                                <>
                                    <code>{opt.domain}</code>
                                    {!opt.is_reachable ? (
                                        <Badge bg="secondary" className="ms-1">
                                            Unreachable
                                        </Badge>
                                    ) : null}
                                    {inactive ? (
                                        <Badge
                                            bg="warning"
                                            text="dark"
                                            className="ms-1"
                                        >
                                            Inactive
                                        </Badge>
                                    ) : null}
                                </>
                            }
                        />
                    );
                })
            )}

            {showAddForm ? (
                <div className="embed-trusted-domain-picker__add-form">
                    <InputGroup
                        size="sm"
                        className="embed-trusted-domain-picker__add-input-group"
                    >
                        <Form.Control
                            type="text"
                            value={newDomain}
                            onChange={(e) =>
                                setNewDomain(
                                    normalizeTrustedDomainInput(e.target.value)
                                )
                            }
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    submitNewDomain(e);
                                } else if (e.key === "Escape") {
                                    cancelAdd();
                                }
                            }}
                            placeholder="example.com"
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="none"
                            spellCheck={false}
                            data-lpignore="true"
                            autoFocus
                            disabled={adding}
                        />
                        <Button
                            type="button"
                            variant={EmbedFormBtn.primary}
                            onClick={submitNewDomain}
                            disabled={adding}
                        >
                            {adding ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                "Add"
                            )}
                        </Button>
                        <Button
                            type="button"
                            variant={EmbedFormBtn.danger}
                            onClick={cancelAdd}
                            disabled={adding}
                        >
                            Cancel
                        </Button>
                    </InputGroup>
                    {addError ? (
                        <Form.Text className="text-danger embed-trusted-domain-picker__add-error">
                            {addError}
                        </Form.Text>
                    ) : (
                        <Form.Text className="embed-trusted-domain-picker__add-hint">
                            No protocol or path. We verify DNS and that the site
                            responds before saving.
                        </Form.Text>
                    )}
                </div>
            ) : !loading ? (
                <div className="embed-trusted-domain-picker__add-toggle">
                    <Button
                        type="button"
                        variant={EmbedFormBtn.secondary}
                        size="sm"
                        onClick={openAddForm}
                    >
                        <BsPlusLg aria-hidden /> Add trusted domain
                    </Button>
                </div>
            ) : null}

            <Form.Text className="embed-trusted-domain-picker__hint">
                Only active domains apply when the widget loads on an external
                site. Manage the full list under{" "}
                <Link to="/embed-ticket-forms/trusted-domains">
                    Trusted domains
                </Link>
                .
            </Form.Text>
        </div>
    );
};

export default EmbedTrustedDomainPicker;
